import React from 'react';

const { REACT_APP_ENV = 'development', REACT_APP_VERSION = 'unspecified' } =
  process.env;

// Bugsnag was removed. This module keeps the same surface so its call sites did
// not have to change, but nothing leaves the browser any more: errors go to the
// console with the breadcrumb trail that preceded them.

export enum BREADCRUMB_TYPE {
  LOG = 'log',
  NAVIGATION = 'navigation',
  REQUEST = 'request',
  PROCESS = 'process',
  STATE = 'state',
  USER = 'user',
  ERROR = 'error',
  MANUAL = 'manual',
}

type Breadcrumb = {
  at: string;
  type: BREADCRUMB_TYPE;
  message: string;
  metadata: Record<string, unknown>;
};

// Bounded so a long session cannot grow this without limit.
const MAX_BREADCRUMBS = 30;
const trail: Breadcrumb[] = [];
let userId: string | null = null;

export function setUser({
  id,
}: {
  id: number;
  email: string | null;
  name: string | null;
}) {
  // Id only. Email and name are accepted because callers already pass them, but
  // they are deliberately not retained.
  userId = id.toString();
}

export function addBreadcrumb({
  message,
  metadata = {},
  type = BREADCRUMB_TYPE.LOG,
}: {
  message: string;
  metadata?: Record<string, unknown>;
  type?: BREADCRUMB_TYPE;
}) {
  trail.push({ at: new Date().toISOString(), type, message, metadata });
  if (trail.length > MAX_BREADCRUMBS) trail.shift();
}

export function reportError(error: Error) {
  // eslint-disable-next-line no-console
  console.error(error, {
    userId,
    releaseStage: REACT_APP_ENV,
    appVersion: REACT_APP_VERSION,
    breadcrumbs: [...trail],
  });
}

// Replaces the boundary the Bugsnag React plugin used to build. Same prop, so
// ApplicationWrapper is unchanged: without this a render error is a blank page.
export class ErrorBoundary extends React.Component<
  { children?: React.ReactNode; FallbackComponent: React.ComponentType },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    reportError(error);
  }

  render() {
    const { FallbackComponent, children } = this.props;
    return this.state.hasError ? <FallbackComponent /> : <>{children}</>;
  }
}
